import { APP_CFG } from '@/configs';
import { MintedNFTEventDto, NftFactory, SuigarNft, mapToNftFactory, mapToSuigarNft } from '@/dtos';
import { mapRawToMintedNftEvent } from '@/dtos/mapper';
import { Transaction } from '@mysten/sui/transactions';
import { SuiClient } from '@mysten/sui/client';
import { normalizeSuiErrorMessage } from '@/utils/errors';

class NftRepository {
	async getNftFactory(client: SuiClient): Promise<NftFactory> {
		const res = await client.getObject({
			id: APP_CFG.NFT_FACTORY,
			options: {
				showContent: true,
			},
		});
		return mapToNftFactory(res.data!);
	}

	async getOwnedNft(client: SuiClient, address: string): Promise<SuigarNft[]> {
		const res = await client.getOwnedObjects({
			owner: address,
			options: {
				showContent: true,
			},
			filter: {
				StructType: `${APP_CFG.PACKAGE}::nft::Nft`,
			},
		});
		return res.data.map(mapToSuigarNft);
	}

	async mintNft(
		specId: string,
		price: number,
		address: string,
		signAndExecuteTransaction: (tx: Transaction) => Promise<any>,
	): Promise<MintedNFTEventDto> {
		const txb = new Transaction();

		const coin = txb.splitCoins(txb.gas, [price]);
		const nft = txb.moveCall({
			arguments: [txb.object(APP_CFG.NFT_FACTORY), txb.object(APP_CFG.HOUSE_HOUSE_SUI_SUI), txb.pure.address(specId), coin],
			target: `${APP_CFG.PACKAGE}::nft::mint_to_sender`,
		});
		txb.transferObjects([nft], txb.pure.address(address));

		const response = await signAndExecuteTransaction(txb);

		const event = (response as any).events?.pop();
		if (!event || (response as any).effects.status.status == 'failure') {
			const error = normalizeSuiErrorMessage((response as any).effects.status.error, { currencySymbol: 'SUI' });
			throw Error(error);
		}
		return mapRawToMintedNftEvent(event.parsedJson, (response as any).digest);
	}

	async transferNft(nftId: string, recipient: string, signAndExecuteTransaction: (tx: Transaction) => Promise<any>) {
		const txb = new Transaction();
		txb.transferObjects([txb.object(nftId)], txb.pure.address(recipient));
		const response = await signAndExecuteTransaction(txb);
		const status = (response as any)?.effects?.status;
		if (status?.status === 'failure') {
			throw new Error(status?.error ?? 'Failed to transfer NFT');
		}
		return response;
	}
}

export const nftRepository = new NftRepository();
